'use client';

import useDriftAccountStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import { useEffect } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { singletonHook } from 'react-singleton-hook';
import { BigNum, SPOT_MARKET_BALANCE_PRECISION_EXP } from '@drift-labs/sdk';
import { sleep } from '@drift/common';
import useInterval from './useInterval';
import Env from 'src/environmentVariables/EnvironmentVariables';

const _useSyncAccountRent = () => {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const connection = useDriftStore((s) => s.connection.current);

	const subaccountsMap = useDriftAccountStore((s) => s.accounts);
	const setAccountsStore = useDriftAccountStore((s) => s.set);
	const subaccounts = Object.values(subaccountsMap);

	const eventEmitter = useDriftStore((s) => s.appEventEmitter);

	const allRentLoaded = subaccounts.every(
		(subaccount) => subaccount?.rent.loaded
	);

	const resetRentsLoaded = () => {
		setAccountsStore((s) => {
			for (const accountKey in s.accounts) {
				if (s.accounts[accountKey]) {
					s.accounts[accountKey].rent.loaded = false;
				}
			}
		});
	};

	const runRefreshAccountRents = () => {
		const subaccountsToSync = subaccounts.filter(
			(subaccount) => !subaccount?.rent.loaded
		) as AccountData[];
		const subaccountsToSyncPubKeys = subaccountsToSync.map(
			(subaccount) => subaccount?.pubKey
		);

		connection
			?.getMultipleAccountsInfo(
				subaccountsToSyncPubKeys,
				Env.defaultCommitmentLevel
			)
			.then((res) => {
				const accounts = res.filter((acc) => !!acc);

				if (accounts.find((accountInfo) => !accountInfo)) {
					return;
				}

				const orderedAccountsRent = accounts.map((accountInfo) =>
					BigNum.from(accountInfo.lamports, SPOT_MARKET_BALANCE_PRECISION_EXP)
				);

				setAccountsStore((s) => {
					orderedAccountsRent.forEach((rent, index) => {
						const userKey = subaccountsToSync[index].userKey ?? '';
						if (s.accounts[userKey]) {
							s.accounts[userKey].rent = {
								amount: rent,
								loaded: true,
							};
						}
					});
				});
			});
	};

	useInterval(() => {
		if (
			!driftClientIsReady ||
			!driftClient ||
			!connection ||
			subaccounts.length === 0 ||
			allRentLoaded
		) {
			return;
		}

		runRefreshAccountRents();
	}, 1000);

	useEffect(() => {
		const handler = async () => {
			await sleep(2000);
			resetRentsLoaded();
		};

		eventEmitter.on('claimedExcessRent', handler);

		return () => {
			eventEmitter.removeListener('claimedExcessRent', handler);
		};
	}, [eventEmitter]);
};

export default singletonHook(undefined, _useSyncAccountRent);
